import { UAParser } from "ua-parser-js";

// Device, OS and browser of the visitor, sent with the attribution payload so the
// Stripe customer and subscription carry them. They go under ua_* keys: utm_device
// is the ad platform's own URL value (m / c / t) and is never written from here.
export interface UserAgentData {
  ua_device?: string;
  ua_os?: string;
  ua_browser?: string;
  ua_in_app?: string;
}

const IN_APP_BROWSERS: Array<[RegExp, string]> = [
  [/FBAN\/|FBAV\/|FB_IAB\/|FBIOS/i, "facebook"],
  [/Instagram/, "instagram"],
  [/GSA\//, "google_app"]
];

// facebook / instagram / google_app, "webview" for another app's web view, "" otherwise.
export const inAppBrowser = (userAgent: string): string => {
  const match = IN_APP_BROWSERS.find(([pattern]) => pattern.test(userAgent));
  if (match) return match[1];

  const androidWebView = /; wv\)/.test(userAgent);
  // iOS apps' web views send no "Safari/" token; Chrome, Firefox and Edge on iOS do.
  const iosWebView =
    /iPhone|iPad|iPod/.test(userAgent) &&
    !/Safari\//.test(userAgent) &&
    !/CriOS\/|FxiOS\/|EdgiOS\//.test(userAgent);
  return androidWebView || iosWebView ? "webview" : "";
};

const joined = (...parts: Array<string | undefined>): string => parts.filter(Boolean).join(" ");

// ua-parser-js 1.x calls it "Mac OS"; Apple's name, which the API's parser and the
// other apps use, is macOS.
const osName = (name?: string): string | undefined => (name === "Mac OS" ? "macOS" : name);

// Never throws: attribution is optional and must not break a signup or checkout.
// Empty values are left out.
export const getUserAgentData = (userAgent?: string): UserAgentData => {
  try {
    const ua = userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : "");
    if (!ua) return {};

    const { device, os, browser } = new UAParser(ua).getResult();
    const data: UserAgentData = {
      ua_device: device.model || device.type || "desktop",
      ua_os: joined(osName(os.name), os.version),
      ua_browser: joined(browser.name, browser.version),
      ua_in_app: inAppBrowser(ua)
    };
    return Object.fromEntries(Object.entries(data).filter(([, value]) => value)) as UserAgentData;
  } catch (_) {
    return {};
  }
};
